import Routes, { routeNames } from '@/constants/Routes';
import { t } from '@/i18n';
import type { IProblemDetails } from '@/types/interfaces/AxiosTypes';
import type { LocationQuery, RouteLocation, RouteLocationRaw, RouteMeta, Router } from 'vue-router';

/** The query parameter the login pages carry the page to continue with after logging in. */
export const returnUrlParameter = 'returnUrl';

const knownRoutes = new Set(routeNames());

/** The query that makes the login pages continue with `returnUrl`; empty for no url or home, the default anyway. */
export function returnUrlQuery(returnUrl?: string): Record<string, string> {
    return returnUrl && returnUrl !== '/' ? { [returnUrlParameter]: returnUrl } : {};
}

/** The login page, continuing with `returnUrl` after login. */
export function loginRoute(returnUrl?: string): RouteLocationRaw {
    return { name: Routes.Login.Root, query: returnUrlQuery(returnUrl) };
}

/**
 * The return url in a login page's query, when it is a page of the app: an app-relative path (not `//host`) that
 * resolves to a named route in `Routes` and isn't itself a login page. Anything else (an unknown path, which only the
 * catch-all matches, or another site) is dropped.
 */
export function resolveReturnUrl(router: Pick<Router, 'resolve'>, query: LocationQuery): RouteLocation | undefined {
    const returnUrl = query[returnUrlParameter];
    if (typeof returnUrl !== 'string' || !returnUrl.startsWith('/') || returnUrl.startsWith('//') || returnUrl.startsWith('/\\')) {
        return;
    }

    try {
        const route = router.resolve(returnUrl);
        if (typeof route.name === 'string' && knownRoutes.has(route.name) && !route.meta.anonymous) {
            return route;
        }
    } catch {
        // A path the router can't parse is no page of the app either.
    }
}

/**
 * Where a login continues: the MFA step when required (passing the return url on), else the return url when this
 * user may open it, else the driver page for driver-only users, else home.
 */
export function routeAfterLogin(
    auth: { isDriverOnly: boolean, hasRole(roles: NonNullable<RouteMeta['roles']>): boolean },
    requireMfa = false,
    returnUrl?: RouteLocation
): RouteLocationRaw {
    if (requireMfa) {
        return { name: Routes.Login.Mfa, query: returnUrlQuery(returnUrl?.fullPath) };
    }

    if (returnUrl && (!returnUrl.meta.roles || auth.hasRole(returnUrl.meta.roles))) {
        return returnUrl.fullPath;
    }

    return { name: auth.isDriverOnly ? Routes.Driver.Root : Routes.Home.Root };
}

/** The login error to show: the server's explanation, or a generic one. */
export function loginErrorMessage(problem?: IProblemDetails): string {
    return problem?.detail ?? t('errors.login');
}
